import { useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  getCompanyMcpSsoAccessQuery,
  getProfessionalLevelsQuery,
  getStackTypeQuery,
  getTeamMembersQuery,
  getTeamRoleQuery,
  refreshUserDataQuery,
} from "@query";
import { TEAM_ROLES } from "@utils";
import { getApiErrorMessage } from "@client";
import { useStores } from "@hooks";
import { CircularProgress, Grid } from "@material-ui/core";
import { Alert, Table, TableCell, TableRow } from "@components";
import { UserRow } from "./Rows/UserRow";
import useStyle from "./TeamTable.style";
import { SeatsCounter } from "./SeatsCounter";
import { SeatsCounterDetails } from "./SeatsCounterDetails";

export const TeamTable = observer(({
 useTableTitle,
 users: providedUsers,
 handleLoad
}) => {
  const classes = useStyle();
  const {userStore} = useStores();
  const {t} = useTranslation();

  const [ isLoading, setIsLoading ] = useState(true);
  const [ users, setUsers ] = useState(providedUsers || []);
  const [ professionalLevels, setLevels ] = useState([]);
  const [ teamRoles, setRoles ] = useState([]);
  const [ stackTypes, setStackTypes ] = useState([]);
  const [ mcpAccessByUser, setMcpAccessByUser ] = useState({});
  const [ error, setError ] = useState();

  const { isAdmin } = userStore;

  const totalAdmins = useMemo(() => (
    users.reduce((p, c) => (c.admin ? p + 1 : p), 0)
  ), [users]);

  const loadData = async (email = null) => {
    if (!isLoading) setIsLoading(true);

    if (email && email === userStore.data.email) {
      const userData = await refreshUserDataQuery();
      userStore.saveUserData(userData);
    }

    if (!email) await loadInputData();

    if (handleLoad) {
      handleLoad();
    } else {
      const res = await getTeamMembersQuery();
      setUsers(res);
    }

    if (isAdmin) {
      try {
        const access = await getCompanyMcpSsoAccessQuery();
        setMcpAccessByUser(
          Object.fromEntries(access.map(item => [item.userUuid, item])),
        );
      } catch (loadError) {
        setMcpAccessByUser({});
        setError(getApiErrorMessage(loadError, t("errors.network")));
      }
    }

    setIsLoading(false);
  };

  const loadInputData = async () => {
    const r = await Promise.all([
      getProfessionalLevelsQuery(),
      getStackTypeQuery(),
      getTeamRoleQuery(),
    ]);

    setLevels(r[0]);
    setStackTypes(r[1]);
    setRoles(!isAdmin ? r[2].filter(r => r.id !== TEAM_ROLES.ADMIN) : r[2]);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (providedUsers && handleLoad) setUsers(providedUsers);
  }, [providedUsers]);

  return (
    <>
      {useTableTitle && (
        <Grid
          item
          container
          justifyContent="space-between"
          wrap="nowrap"
          className={classes.tableTitle}
        >
          <SeatsCounter isLoading={isLoading} users={users.length}/>
          <SeatsCounterDetails
            isLoading={isLoading}
            users={users.length}
            onInvite={loadData}
          />
        </Grid>
      )}
      {isLoading && (
        <CircularProgress
          aria-label="progress indicator"
          className={classes.spinner}
        />
      )}
      {!isLoading && (
        <Table
          id="teamMembers"
          headers={[
            {label: t("views.team.name"), width: 300, flexible: true},
            {label: t("views.team.role"), width: 160},
            {label: t("views.team.projects_visibility.title"), width: 180},
            {label: t("views.team.status"), width: 80},
            ...(isAdmin
              ? [{label: t("views.team.mcp_sso.column"), width: 150}]
              : []),
            {width: 34},
          ]}
        >
          {users.map(u => (
            <UserRow
              key={u.uuid}
              user={u}
              mcpSsoAccess={mcpAccessByUser[u.uuid]}
              totalAdmins={totalAdmins}
              reloadData={loadData}
              professionalLevels={professionalLevels}
              teamRoles={teamRoles}
              stackTypes={stackTypes}
            />
          ))}
          {!isLoading && !users.length && (
            <TableRow>
              <TableCell colSpan={isAdmin ? 6 : 5}>
                <h3>{t("views.team.not_found")}</h3>
              </TableCell>
            </TableRow>
          )}
        </Table>
      )}
      <Alert
        isOpen={Boolean(error)}
        title={error}
        acceptText={t("common.close")}
        onAccept={() => setError(undefined)}
      />
    </>
  );
});

TeamTable.propTypes = {
  useTableTitle: bool,
  users: arrayOf(object),
  handleLoad: func,
};
